import type {CreateExtensionArgument, DmApis, Extension, ResourceLoaderDefinition} from '@wix/document-manager-core'
import _ from 'lodash'
import type {SchemaExtensionAPI} from './schema/schema'
import type {RMApi} from './rendererModel'
import type {
    GetSchemaCollectionsByAppIdsResponse,
    NamespaceSchema,
    Reference,
    SchemaCollection
} from './csave/serverProtocolTypes'
import type {
    BuilderEditorClientSpecMapEntry,
    ClientSpecMap,
    ComponentSchemasDefinition,
    RefInfo,
    SchemaFile
} from '@wix/document-services-types'
import {RESOURCE_TYPES, DATA_TYPES} from '../constants/constants'
import {schemaConfigs} from '@wix/document-services-json-schemas'
const {getDefaultFriendlyReferencedMap} = schemaConfigs
import {hasBuilderComponents} from '../utils/appUtils'
import {topLevelDataNodeProperties, topLevelStyleNodeProperties} from './builderComponents/manifest'

const INTERACTIONS = {
    LOAD_SCHEMAS: 'load-schemas',
    REGISTER_SCHEMAS: 'register-schemas'
}

export interface SchemaCollectionServer {
    getSchemaCollectionsByAppIds(
        appDefinitionIds: string[],
        overrideIds?: string[],
        cursor?: string
    ): Promise<GetSchemaCollectionsByAppIdsResponse>
}

export const createExtension = ({
    environmentContext,
    logger,
    experimentInstance
}: CreateExtensionArgument): Extension => {
    const createResourceLoader = (dmAPIs: DmApis): ResourceLoaderDefinition[] => {
        const {dal, pointers} = dmAPIs
        const {schemaAPI, rendererModel} = dmAPIs.extensionAPI as SchemaExtensionAPI & RMApi

        const mapReferencesToRefInfo = (references: Reference[]): RefInfo[] => {
            return references.map(({list, refOwner, referencedMap, ...reference}) => ({
                ...reference,
                isList: list,
                isRefOwner: refOwner,
                referencedMap: getDefaultFriendlyReferencedMap(referencedMap!)
            })) as RefInfo[]
        }

        const convertNamespaceToSchemaRecord = (namespace: NamespaceSchema): Record<string, SchemaFile> => {
            if (!namespace) {
                return {}
            }

            const result: Record<string, SchemaFile> = {}

            if (namespace.type && namespace.jsonSchema) {
                result[namespace.type] = namespace.jsonSchema as SchemaFile
            }

            if (namespace.derivedNamespaces && Array.isArray(namespace.derivedNamespaces)) {
                for (const derivedNamespaceData of namespace.derivedNamespaces) {
                    if (derivedNamespaceData?.type && derivedNamespaceData.jsonSchema) {
                        result[derivedNamespaceData.type] = derivedNamespaceData.jsonSchema as SchemaFile
                    }
                }
            }

            return result
        }

        const getReferencesFromNamespace = (namespaceValue: NamespaceSchema): Record<string, Reference[]> => {
            const result: Record<string, Reference[]> = {}

            if (namespaceValue?.references && namespaceValue.type) {
                result[namespaceValue.type] = namespaceValue.references
            }

            if (namespaceValue?.derivedNamespaces && Array.isArray(namespaceValue.derivedNamespaces)) {
                for (const derivedNamespaceData of namespaceValue.derivedNamespaces) {
                    if (derivedNamespaceData?.references && derivedNamespaceData.type) {
                        result[derivedNamespaceData.type] = derivedNamespaceData.references
                    }
                }
            }

            return result
        }

        const createDraftStyleSchemaOverride = (variableConnections?: any): SchemaFile =>
            ({
                type: ['object', 'null'],
                additionalProperties: false,
                isTopLevelDataNode: true,
                properties: {
                    ...topLevelDataNodeProperties(),
                    style: {
                        type: 'object',
                        additionalProperties: true,
                        properties: {},
                        isDraftSchema: true
                    },
                    name: {
                        type: 'string',
                        maxLength: 100
                    },
                    ...topLevelStyleNodeProperties(),
                    ...(!!variableConnections && {variableConnections})
                },
                isDraftSchema: true
            } as any as SchemaFile)

        const registerSchemaCollection = (schemaCollection: SchemaCollection) => {
            const {id, definition, namespaces} = schemaCollection

            if (definition?.type === 'LegacyComponent' && definition.builderTypes?.referredType) {
                const referredDefinition = schemaAPI.getDefinition(definition.builderTypes.referredType)
                referredDefinition.aliases.push(id!)
                schemaAPI.registerComponentDefinitionAndSchemas(
                    definition!.builderTypes.referredType,
                    {
                        componentDefinition: {
                            [definition.builderTypes.referredType]: referredDefinition
                        },
                        dataSchemas: {},
                        propertiesSchemas: {}
                    },
                    true
                )
                return
            }

            const convertedNamespaces = Object.entries(namespaces ?? {}).reduce(
                (acc, [namespace, namespaceValue]: [string, NamespaceSchema]) => {
                    acc[namespace] = convertNamespaceToSchemaRecord(namespaceValue)
                    return acc
                },
                {} as Record<string, Record<string, SchemaFile>>
            )

            if (convertedNamespaces[DATA_TYPES.theme]) {
                const styleSchemas = convertedNamespaces[DATA_TYPES.theme]
                const overriddenStyleSchemas: Record<string, SchemaFile> = {}

                for (const [schemaName, originalSchema] of Object.entries(styleSchemas)) {
                    const variableConnections = (originalSchema as Record<string, any>)?.properties?.variableConnections

                    overriddenStyleSchemas[schemaName] = createDraftStyleSchemaOverride(variableConnections)
                }

                convertedNamespaces[DATA_TYPES.theme] = overriddenStyleSchemas
            }

            const {data, ...otherSchemas} = convertedNamespaces

            const componentSchemasDefinition: ComponentSchemasDefinition = {
                componentDefinition: {
                    [id!]: definition
                },
                dataSchemas: data || {},
                propertiesSchemas: {},
                otherSchemas
            }
            schemaAPI.registerComponentDefinitionAndSchemas(id!, componentSchemasDefinition, true)

            const allReferences = Object.entries(namespaces ?? {}).reduce(
                (acc, [namespace, namespaceValue]: [string, NamespaceSchema]) => {
                    const references = getReferencesFromNamespace(namespaceValue)
                    acc[namespace] = acc[namespace] || {}
                    Object.assign(acc[namespace], references)

                    return acc
                },
                {} as Record<string, Record<string, Reference[]>>
            )

            for (const [namespace, typeToReferences] of Object.entries(allReferences)) {
                for (const [type, references] of Object.entries(typeToReferences)) {
                    const refsInfo = mapReferencesToRefInfo(references)
                    schemaAPI.addReferences(namespace, type, refsInfo)
                }
            }
        }

        const load = async (appDefIds: string[]) => {
            if (!appDefIds.length) {
                return
            }

            const overrideIds: string[] = []
            for (const appDefId of appDefIds) {
                const overrideId = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefId))?.appFields
                    ?.overrideId
                if (overrideId) overrideIds.push(overrideId)
            }

            try {
                const allSchemaCollections: SchemaCollection[] = []
                let cursor: string | undefined | null
                let hasNext: boolean = true

                logger.interactionStarted(INTERACTIONS.LOAD_SCHEMAS)

                while (hasNext) {
                    const response: GetSchemaCollectionsByAppIdsResponse =
                        await environmentContext.serverFacade.getSchemaCollectionsByAppIds(
                            appDefIds,
                            overrideIds.length > 0 ? overrideIds : undefined,
                            cursor
                        )

                    if (response.schemaCollections) {
                        allSchemaCollections.push(...response.schemaCollections)
                    }

                    cursor = response.pagingMetadata?.cursors?.next
                    hasNext = !!response.pagingMetadata?.hasNext
                }

                logger.interactionStarted(INTERACTIONS.REGISTER_SCHEMAS)

                for (const schemaCollection of allSchemaCollections) {
                    const {innerElements} = schemaCollection
                    registerSchemaCollection(schemaCollection)
                    if (innerElements) {
                        for (const innerElement of innerElements) {
                            registerSchemaCollection(innerElement)
                        }
                    }
                }

                logger.interactionEnded(INTERACTIONS.REGISTER_SCHEMAS)
                logger.interactionEnded(INTERACTIONS.LOAD_SCHEMAS)
            } catch (error) {
                logger.interactionEnded(INTERACTIONS.REGISTER_SCHEMAS)
                logger.interactionEnded(INTERACTIONS.LOAD_SCHEMAS)
                throw error
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            return _.uniq(
                Object.values(clientSpecMap)
                    .filter(application => hasBuilderComponents(application as BuilderEditorClientSpecMapEntry))
                    .map(appSpec => (appSpec as BuilderEditorClientSpecMapEntry).appDefinitionId!)
                    .filter(Boolean)
            )
        }

        const loadAll = async () => {
            await load(getAvailableResources())
        }

        const initAvailableResources = async () => {
            if (!experimentInstance.isOpen('dm_schemaResourceLoader')) {
                return
            }

            await loadAll()
        }

        return [
            {
                resourceType: RESOURCE_TYPES.SCHEMA,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    return {
        name: 'schemaResourceLoader',
        dependencies: new Set(['schema', 'rendererModel']),
        createResourceLoader
    }
}
